import { css } from 'twin.macro';
import React, { Fragment, useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaLink, FaKey, FaCopy, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Switch } from '../../../_elements/FormElements';
import { BootstrapToolTip } from '../../../_elements/ToolTip';
import DateTimePicker from 'react-datetime-picker/dist/DateTimePicker';
import loaderStyles from '../../../_elements/loader';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import CopyableText from '@/components/ShareBtn/CopyableText';
import Swal from 'sweetalert2';
import { sharedUserFormDataApi } from '@/_services/api/sharedUserFormData';

const modalStyles = css`
    .modal-content {
        padding: 2rem 1rem;
    }
`;

const StyledModalDialog = styled.div`
    &.modal-90w {
        max-width: 800px;
        background: #fff;
        margin: auto;
        box-shadow:
            rgba(0, 0, 0, 0.25) 0px 54px 55px,
            rgba(0, 0, 0, 0.12) 0px -12px 30px,
            rgba(0, 0, 0, 0.12) 0px 4px 6px,
            rgba(0, 0, 0, 0.17) 0px 12px 13px,
            rgba(0, 0, 0, 0.09) 0px -3px 5px;
    }
`;

const CodeContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 6px;
    margin-right: 12px;
`;

const ActionSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 2rem;
`;

function ShareUserFormData({ userFormData = {}, ...props }) {
    const [show, setShow] = useState(false);
    const [sharedForm, setSharedForm] = useState({});
    const [isBusy, setIsBusy] = useState(false);
    const [endDate, setEndDate] = useState(null);
    const [isAcceptingResponses, setIsAcceptingResponses] = useState(true);
    const userFormDataId = userFormData?._id;
    const roomId = userFormData?.chatroom;
    const formId = userFormData?.form;

    useEffect(() => {
        if (props.checkIsShowing) props.checkIsShowing(show);
    }, [props, show]);

    const handleClose = () => {
        if (props?.handleFormClosed) props.handleFormClosed();
        setShow(false);
    };

    const handleShow = () => {
        setShow(true);
        if (props.handleFormOpened) props.handleFormOpened();
    };

    const getValidEndDate = (date = null) => date?.toISOString();

    const fetchSharedForm = async (userformdataId, roomId) => {
        try {
            setIsBusy(true);

            const response = await sharedUserFormDataApi.getSharedUserFormData({
                userformdataId,
                chatroom: roomId,
            });

            if (response?._id) {
                setSharedForm(response);
                setIsAcceptingResponses(response?.acceptResponse);
                setEndDate(response?.endDate ? new Date(response?.endDate) : null);
            }
        } catch (err) {
            if (err?.name === 'AbortError') return;
            console.error(err);
            toast.error(err?.message);
        } finally {
            setIsBusy(false);
        }
    };

    useEffect(() => {
        if (userFormDataId && roomId && show) {
            fetchSharedForm(userFormDataId, roomId);
        }
    }, [roomId, userFormDataId, show]);

    const generateLink = async (e) => {
        e.preventDefault();
        try {
            setIsBusy(true);
            const payload = {
                endDate: getValidEndDate(endDate),
                chatroom: roomId,
                acceptResponse: isAcceptingResponses,
                userformdata: userFormDataId,
                form: formId,
            };
            const response = await sharedUserFormDataApi.generateSharedUserFormLink({ payload });
            setSharedForm(response || {});
            toast.success('Shareable link generated successfully!');
        } catch (error) {
            toast.error(error?.message);
        } finally {
            setIsBusy(false);
        }
    };

    const updatePublicForm = async (sharedFormId) => {
        try {
            setIsBusy(true);
            const payload = { endDate: getValidEndDate(endDate), acceptResponse: isAcceptingResponses };
            const response = await sharedUserFormDataApi.updateSharedUserFormData({
                sharedUserFormDataId: sharedFormId,
                payload,
            });
            setSharedForm(response || {});
            setIsAcceptingResponses(response?.acceptResponse);
            setEndDate(response?.endDate ? new Date(response?.endDate) : endDate);
            toast.success('Updated successfully!');
        } catch (err) {
            console.error(err);
            toast.error(err?.message);
        } finally {
            setIsBusy(false);
        }
    };

    const generateFormAccessCode = async () => {
        try {
            setIsBusy(true);
            const response = await sharedUserFormDataApi.generateFormAccessCode({
                sharedUserFormDataId: sharedForm._id,
            });
            setSharedForm((prev) => ({ ...prev, formAccessCode: response.formAccessCode }));
            toast.success('Form Access code generated successfully!');
        } catch (err) {
            console.error(err);
            toast.error(err?.message);
        } finally {
            setIsBusy(false);
        }
    };

    const removeFormAccessCode = async () => {
        try {
            setIsBusy(true);
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: `Removing the code will allow anyone with link to access the form.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!',
            });

            if (result.isConfirmed) {
                await sharedUserFormDataApi.removeFormAccessCode({
                    sharedUserFormDataId: sharedForm._id,
                });
                setSharedForm((prev) => ({ ...prev, formAccessCode: null }));
                toast.success('Form access code removed!');
            }
        } catch (err) {
            console.error(err);
            toast.error(err?.message);
        } finally {
            setIsBusy(false);
        }
    };

    const copyToClipboard = async (text, type = 'url') => {
        try {
            await navigator.clipboard.writeText(text);
            toast.info('Copied to clipboard!');
        } catch (err) {
            console.error('Failed to copy:', err);
            toast.error('Failed to copy to clipboard');
        }
    };

    return (
        <Fragment>
            <BootstrapToolTip text="Share Submitted Form Url">
                {props?.compact ? (
                    <FaLink
                        size={20}
                        color={'#50aeb0'}
                        onClick={handleShow}
                        css={css`
                            &:hover {
                                opacity: 0.7;
                            }
                        `}
                    />
                ) : (
                    <Button
                        onClick={handleShow}
                        variant="outline-primary"
                        className="pointer d-flex outline-primary"
                        css={css`
                            align-items: center;
                            gap: 6px;
                            flex-wrap: wrap;
                            @media (max-width: 600px) {
                                justify-content: center;
                            }
                            border-radius: 16px;
                            padding: 2px 10px;
                            margin-left: 5px;
                            font-size: 1.2rem;
                        `}
                    >
                        <FaLink /> Share
                    </Button>
                )}
            </BootstrapToolTip>

            <Modal
                show={show}
                onHide={handleClose}
                backdrop="static"
                css={modalStyles}
                centered
                size="lg"
                dialogClassName="modal-90w"
                dialogAs={StyledModalDialog}
            >
                <Modal.Header closeButton>
                    <div
                        css={css`
                            text-align: center;
                            width: 100%;
                            margin-bottom: 1rem;
                        `}
                    >
                        <h3>
                            <b>{props?.form?.name}</b>
                        </h3>

                        {sharedForm?.createdAt && (
                            <i>Last updated: {dayjs(sharedForm?.updatedAt).format('MMMM D, YYYY h:mm A')}</i>
                        )}
                    </div>
                </Modal.Header>

                <Modal.Body className="position-relative">
                    {isBusy && (
                        <div
                            css={css`
                                height: 100%;
                                width: 100%;
                                position: absolute;
                                top: 0;
                                left: 0;
                                background: rgba(255, 255, 255, 0.8);
                                z-index: 10;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                            `}
                        >
                            <div css={loaderStyles}>
                                <img src="/images/loading.gif" alt="loader" />
                            </div>
                        </div>
                    )}

                    <Form>
                        <Form.Group controlId="formExpirationDate">
                            <div
                                css={css`
                                    display: flex;
                                    justify-content: space-between;
                                    align-items: center;
                                    margin-bottom: 1.5rem;
                                `}
                            >
                                <Form.Label className="mb-0">
                                    <b>Stop Access At:</b>
                                </Form.Label>
                                <DateTimePicker
                                    css={css`
                                        width: 280px;
                                    `}
                                    onChange={setEndDate}
                                    minDate={endDate || new Date()}
                                    value={endDate}
                                    format="MM-dd-y hh:mm a"
                                    disableClock
                                    calendarType="US"
                                />
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <div className="d-flex align-items-center">
                                <Switch
                                    id="acceptingResponses"
                                    label={
                                        isAcceptingResponses
                                            ? 'Enabled/Accepting Responses'
                                            : 'Disabled/Not Accepting Responses'
                                    }
                                    checked={isAcceptingResponses}
                                    onChange={() => setIsAcceptingResponses(!isAcceptingResponses)}
                                    disabled={isBusy}
                                    labelStyle={`font-weight: 500; ${isAcceptingResponses ? 'color: #28a745;' : 'color: #dc3545;'}`}
                                    disableTextClick
                                />
                            </div>
                        </Form.Group>
                        <div style={{ marginTop: '5rem' }}></div>
                        {sharedForm?.url && (
                            <CopyableText
                                text={sharedForm.url}
                                label="Shareable Link:"
                                isLink={true}
                                margin="1rem 0 0 0"
                            />
                        )}

                        <ActionSection>
                            <div className="d-flex align-items-center">
                                {sharedForm?.url && sharedForm?.formAccessCode && (
                                    <CodeContainer>
                                        <span className="mr-2">
                                            Access Code: <strong>{sharedForm.formAccessCode}</strong>
                                        </span>

                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            disabled={isBusy}
                                            onClick={() => copyToClipboard(sharedForm.formAccessCode, 'code')}
                                        >
                                            <FaCopy />
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            disabled={isBusy}
                                            onClick={removeFormAccessCode}
                                            css={css`
                                                margin-left: 4px;
                                            `}
                                        >
                                            <FaTrash />
                                        </Button>
                                    </CodeContainer>
                                )}
                                {sharedForm?.url && !sharedForm?.formAccessCode && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        disabled={isBusy}
                                        onClick={generateFormAccessCode}
                                        css={css`
                                            display: flex;
                                            align-items: center;
                                            gap: 4px;
                                        `}
                                    >
                                        <FaKey /> Generate Code
                                    </Button>
                                )}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                {sharedForm?.url ? (
                                    <Button
                                        variant="primary"
                                        disabled={isBusy}
                                        onClick={() => updatePublicForm(sharedForm._id)}
                                    >
                                        Update Changes
                                    </Button>
                                ) : (
                                    <Button variant="primary" disabled={isBusy} onClick={generateLink}>
                                        Generate Link
                                    </Button>
                                )}
                            </div>
                        </ActionSection>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <div
                        css={css`
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            width: 100%;
                        `}
                    >
                        <p
                            css={css`
                                font-size: 1.2rem;
                                margin: 0;
                            `}
                        >
                            Note: Users can update the form content while in draft mode using the url
                        </p>
                        <Button variant="secondary" onClick={handleClose} disabled={isBusy}>
                            Close
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
}

export default ShareUserFormData;
